/* eslint-disable no-nested-ternary */
import React from 'react';
import dayjs from 'dayjs';
import { Space, Typography, Tag, Button } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import currencyFormat from 'utils/currencyFormat';
import { REFERRAL_TYPE } from 'constants/status';
import {
  TransactionIcon1SvgIcon,
  TransactionIcon2SvgIcon,
  TransactionIcon3SvgIcon,
  TransactionIcon4SvgIcon,
  TransactionRectangleIcon1SvgIcon,
  TransactionRectangleIcon2SvgIcon,
  TransactionRectangleIcon3SvgIcon,
  TransactionRectangleIcon4SvgIcon
} from 'utils/icons';

const TOUR_TRANSACTION_CONTENT = [
  {
    content: (
      <div>
        <h2>Welcome</h2>
      </div>
    ),
    placement: 'center',
    target: 'body'
  },
  {
    title: 'Search & Filter',
    content: 'Use criteria based search and filters to view mode-wise transaction details',
    placement: 'right',
    target: '.ebz-filters-btn'
  },
  {
    content: (
      <div>
        <h2>End</h2>
      </div>
    ),
    placement: 'center',
    target: 'body'
  }
];

const columnStyles = {
  fontWeight: 700,
  fontSize: '14px',
  textAlign: 'center',
  width: '100%',
  whiteSpace: 'nowrap'
};

const textStyle = {
  fontSize: '14px',
  width: '100%'
};

const getTransactionColumns = (handleViewDetails) => {
  /* eslint-disable camelcase */

  return [
    {
      title: <Space style={columnStyles}>Easebuzz ID</Space>,
      dataIndex: 'app_transaction_id',
      key: 'app_transaction_id',
      fixed: 'left',
      render: (app_transaction_id) => {
        return <Typography.Text style={textStyle}>{app_transaction_id}</Typography.Text>;
      },
      width: 190
    },
    {
      title: <Space style={columnStyles}>Referral ID</Space>,
      dataIndex: 'reffered_id',
      key: 'reffered_id',
      render: (reffered_id) => {
        return <Typography.Text style={textStyle}>{reffered_id}</Typography.Text>;
      },
      width: 130
    },
    {
      title: <Space style={columnStyles}>Referral Name</Space>,
      dataIndex: 'referred_name',
      key: 'referred_name',
      render: (referred_name) => {
        return <Typography.Text style={textStyle}>{referred_name ?? 'NA'}</Typography.Text>;
      },
      width: 200
    },
    {
      title: <Space style={columnStyles}>Referral Type</Space>,
      dataIndex: 'referred_type',
      key: 'referred_type',
      render: (referredType) => {
        return referredType ? (
          <Tag
            color={REFERRAL_TYPE[referredType].bgColor}
            style={{
              padding: '2px 13px',
              color: REFERRAL_TYPE[referredType].color,
              fontSize: '12px'
            }}
          >
            {REFERRAL_TYPE[referredType].label}
          </Tag>
        ) : (
          <>
            <Tag
              color="#EBEBEB"
              style={{ padding: '2px 13px', color: '#7B7B7B', fontSize: '12px' }}
            >
              ----
            </Tag>
            {/* {c} */}
          </>
        );
      },
      width: 190
    },
    {
      title: <Space style={columnStyles}> Transaction Date</Space>,
      dataIndex: 'transaction_ist_date',
      align: 'left',
      key: 'transaction_ist_date',
      render: (transaction_ist_date) => {
        return transaction_ist_date === null ? (
          'Invalid Date'
        ) : (
          <Typography.Text style={textStyle}>
            {dayjs(transaction_ist_date).format('DD MMM, YYYY')}
          </Typography.Text>
        );
      },
      width: 190
    },
    {
      title: <Space style={columnStyles}>Transaction Amt (₹)</Space>,
      dataIndex: 'transaction_amount',
      key: 'transaction_amount',
      render: (e) => {
        return <Typography.Text style={textStyle}>{currencyFormat(e, true, true)}</Typography.Text>;
      },
      width: 200
    },
    {
      title: <Space style={columnStyles}>Commission Amt (₹)</Space>,
      dataIndex: 'commission_amount',
      key: 'commission_amount',
      render: (commission_amount) => {
        return (
          <Typography.Text style={textStyle}>
            {currencyFormat(commission_amount, true, true)}
          </Typography.Text>
        );
      },
      width: 200
    },
    {
      title: <Space style={columnStyles}>Payment Mode</Space>,
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      render: (transaction_type) => {
        return <Typography.Text style={textStyle}>{transaction_type}</Typography.Text>;
      },
      width: 190
    },
    {
      title: <Space style={columnStyles}>Transaction Ref. No.</Space>,
      dataIndex: 'transaction_ref_number',
      key: 'transaction_ref_number',
      ellipsis: {
        showTitle: true
      },
      render: (transaction_ref_number) => {
        return <Typography.Text style={textStyle}>{transaction_ref_number}</Typography.Text>;
      },
      width: 200
    },
    {
      title: <Space style={columnStyles}>Bank Ref. No.</Space>,
      dataIndex: 'bank_ref_number',
      key: 'bank_ref_number',
      ellipsis: {
        showTitle: true
      },
      render: (bank_ref_number) => {
        return <Typography.Text style={textStyle}>{bank_ref_number}</Typography.Text>;
      },
      width: 190
    },
    {
      title: <Space style={columnStyles}>Details</Space>,
      fixed: 'right',
      align: 'center',
      render: (transactionInfo) => {
        return (
          <div>
            <Button
              size="medimum"
              style={{ borderColor: 'var(--color-primary)' }}
              onClick={() => handleViewDetails(transactionInfo)}
              icon={
                <EyeOutlined
                  style={{ color: 'var(--color-primary)', cursor: 'pointer' }}
                  onClick={() => handleViewDetails(transactionInfo)}
                />
              }
            />
          </div>
        );
      },
      width: 100
    }
  ];
};

const getTransactionSummaryCards = (tarnsactionSummaryCards) => {
  return [
    {
      label: 'Total Transaction Count',
      value: tarnsactionSummaryCards?.total_transaction_count ?? 0,
      icon: <TransactionIcon1SvgIcon style={{ fontSize: '40px' }} />,
      rect: (
        <div className="transaction-rectangle-icon">
          <TransactionRectangleIcon1SvgIcon
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover' // ensures it fills the div nicely
            }}
          />
        </div>
      )
    },
    {
      label: 'Gross Transaction Value',
      value: currencyFormat(tarnsactionSummaryCards?.total_transaction_amount ?? 0, true, true),
      icon: <TransactionIcon2SvgIcon style={{ fontSize: '40px' }} />,
      rect: (
        <div className="transaction-rectangle-icon">
          <TransactionRectangleIcon2SvgIcon
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover' // ensures it fills the div nicely
            }}
          />
        </div>
      )
    },
    {
      label: 'Total Commission Expected',
      value: currencyFormat(
        tarnsactionSummaryCards?.total_commission_amount_expected ?? 0,
        true,
        true
      ),
      icon: <TransactionIcon4SvgIcon style={{ fontSize: '40px' }} />,
      rect: (
        <div className="transaction-rectangle-icon">
          <TransactionRectangleIcon4SvgIcon
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover' // ensures it fills the div nicely
            }}
          />
        </div>
      )
    },
    {
      label: 'Total Commission Earned',
      value: currencyFormat(
        tarnsactionSummaryCards?.total_commission_amount_earned ?? 0,
        true,
        true
      ),
      icon: <TransactionIcon3SvgIcon style={{ fontSize: '40px' }} />,
      rect: (
        <div className="transaction-rectangle-icon">
          <TransactionRectangleIcon3SvgIcon
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover' // ensures it fills the div nicely
            }}
          />
        </div>
      )
    }
  ];
};

const getTransactionSearchList = (hasSubmerchantData) => {
  return [
    {
      label: 'Easebuzz ID',
      value: 'app_transaction_id'
    },
    {
      label: 'Merchant ID',
      value: 'merchant_id'
    },
    ...(hasSubmerchantData
      ? [
          {
            label: 'Sub-Merchant ID',
            value: 'submerchant_id'
          }
        ]
      : []),
    {
      label: 'Bank Reference Number',
      value: 'bank_ref_number'
    },
    {
      label: 'Partner Payout ID',
      value: 'partner_payout_id'
    }
  ];
};

export {
  TOUR_TRANSACTION_CONTENT,
  getTransactionColumns,
  getTransactionSummaryCards,
  getTransactionSearchList
};
